import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pmGet, pmPost, type PmProject } from '../../api';

/** One initiative's completion state and work-item rollup. Mirrors the server's shape. */
interface EccInitiative {
  opportunity_id: string;
  name: string;
  closed: boolean;
  completed_at: string | null;
  completed_by_name: string | null;
  closure_note: string | null;
  work_items_total: number;
  work_items_done: number;
  all_done: boolean;
  no_work_items: boolean;
}
import {
  DOW_NAMES,
  EV_RECUR,
  EV_TYPES,
  PHASE_COLORS,
  PHASE_LABEL,
  RISK_SEV,
  RISK_STATUS,
  STATUSES,
  STATUS_COLOR,
  STATUS_ICON,
  autoStatus,
  expandOccurrences,
  fmtShort,
  pctOf,
  pd,
  progBgVal,
  today,
  utilColor,
  type EccData,
  type EccDeliverable,
  type EccEvent,
  type EccRisk,
} from './eccLib';
import {
  EccBacklog,
  EccCalendar,
  EccGantt,
  EccGovernance,
  EccRiskList,
  EccUtilization,
} from './EccPanes';
import './ExecutionControlCenter.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../../finalizedRuleHelp';

/**
 * Execution Control Center — the live command centre for one delivery project.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/tracker.ejs`.
 * The screen's own description, from its help panel:
 *
 *   The live command center for a work item project. Track work item progress, deadlines and
 *   a calendar, manage risks and governance events, and view a Gantt, budget and resources —
 *   all in one place.
 *
 * TWO PANES. The left is a tree grid of phases and their work items, each with a drag
 * slider; the right is one of nine modes. Selecting a work item on the left switches the
 * right pane to Details, and clicking the same item again deselects it.
 *
 * PROGRESS SAVES ON RELEASE, NOT ON DRAG. `onInput` updates the local model and repaints;
 * `onChange` is what calls the API. Dragging a slider from 0 to 80 is therefore ONE write,
 * not eighty.
 *
 * SCOPE IS THREE-WAY and the source's default is "All opportunities". The Global checkbox is
 * opt-in and OFF by default, adding the programme's shared work items as a synthetic Global
 * phase group. Events, risks and budget stay project-level across every scope.
 *
 * STATE PERSISTS PER PROJECT in sessionStorage — but is only RESTORED when arriving back
 * from a tracker via `?project=`. Any other arrival starts collapsed, which is the source's
 * behaviour and the reason `cameFromTracker` exists.
 */

type Mode =
  | 'overview'
  | 'backlog'
  | 'gantt'
  | 'calendar'
  | 'details'
  | 'risk'
  | 'governance'
  | 'budget'
  | 'resources';

const MODES: { key: Mode; icon: string; label: string }[] = [
  { key: 'overview', icon: 'dashboard', label: 'Overview' },
  { key: 'backlog', icon: 'list_alt', label: 'Backlog' },
  { key: 'gantt', icon: 'view_timeline', label: 'Gantt Chart' },
  { key: 'calendar', icon: 'calendar_month', label: 'Calendar' },
  { key: 'details', icon: 'description', label: 'Details' },
  { key: 'risk', icon: 'warning', label: 'Risk' },
  { key: 'governance', icon: 'groups', label: 'Governance' },
  { key: 'budget', icon: 'account_balance_wallet', label: 'Budget' },
  { key: 'resources', icon: 'groups_2', label: 'Resources' },
];

interface OppOption {
  opportunity_id: string;
  name: string;
  methodology: string;
}

interface BrStats {
  budget: { planned: number; actual: number; util: number };
  resource: { planned: number; actual: number; util: number };
}

const eccKey = (id: string) => `eccState:${id}`;

/** The donut used for Overall Completion. Cloned from the source's `ringSvg`. */
function RingSvg({ pct, size, color }: { pct: number; size: number; color: string }) {
  const r = size / 2 - 7;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eceff1" strokeWidth={7} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={7}
        strokeLinecap="round"
        strokeDasharray={c.toFixed(1)}
        strokeDashoffset={off.toFixed(1)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset .3s' }}
      />
      <text
        x={size / 2}
        y={size / 2 + 4}
        textAnchor="middle"
        fontSize={size * 0.26}
        fontWeight={800}
        fill="#37474f"
      >
        {pct}
        <tspan fontSize={size * 0.15}>%</tspan>
      </text>
    </svg>
  );
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/tracker.ejs`,
 * its `trackerHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const ECC_HELP: HelpContent = {
    title: 'Execution Control Center',
    icon: '📊',
    purpose: "The <strong>live command center</strong> for a project. Track <strong>work item progress</strong>, deadlines and a calendar, manage <strong>risks</strong> and <strong>governance events</strong>, and view a Gantt, budget and resources &mdash; all in one place.",
    doHere: [
      "Select a project, then set each work item's <strong>progress %</strong> in the left pane, or tick <strong>100%</strong> to take it straight to full. <strong>Status is yours to set</strong> &mdash; choose it in the <strong>Details</strong> pane; it is never derived from progress.",
      "Pick an <strong>Opportunity</strong> to track its work items; tick <strong>Global</strong> to also pull in the shared/project work items as a single <strong>Global / Shared</strong> group &mdash; project&#8209;level work items have no phases.",
      "Switch the right pane between <strong>Overview, Gantt Chart, Calendar, Details, Risk, Governance, Budget</strong> and <strong>Resources</strong>.",
      "Log <strong>governance events</strong> (Steering Committees, Gate Reviews, Milestones, Meetings, Workshops) and a <strong>RAID/Risk</strong> register.",
      "Watch KPIs &mdash; status counts, overdue items and the next milestone &mdash; update automatically.",
      "<strong>Mark the initiative complete</strong> when it is finished. With a single opportunity in scope, a band under the work item list shows how many of its work items are done. The button is always clickable &mdash; if any work item is still open it tells you exactly what is outstanding. Marking it complete stamps the date and your name, drops the initiative out of <strong>Portfolio Exceptions</strong>, and is required before the project can be completed on <strong>Project Completion</strong>. An initiative is never <em>closed</em> &mdash; only a project is. An initiative is <strong>complete</strong> or <strong>not complete</strong>, and only you set that.",
      "Arriving from <strong>Portfolio Exceptions</strong> via <strong>Open</strong>? The project, its opportunity and the work item are selected for you, and the item is scrolled into view on the left."
    ],
    fits: 'Budget &amp; Resource Plan &rarr; <strong>Execution Control Center</strong> &rarr; Budget Tracker &rarr; Resource Tracker',
    firstTime: "Pick a project, set the <strong>progress %</strong> on a work item (or tick <strong>100%</strong>), then explore the <strong>Overview</strong> and <strong>Gantt</strong> views.",
    sections: [
      {
        label: 'Scope — by opportunity, with optional Global',
        type: 'html',
        html: "The <strong>Opportunity</strong> selector chooses which opportunity's work items you track (it defaults to the first one). Tick the <strong>Global</strong> checkbox to also include the project's <strong>shared/global</strong> work items (those not tied to one opportunity) — they appear as a single <strong>Global / Shared</strong> group alongside the opportunity's phases. Project&#8209;level work items belong to no opportunity, so they carry <strong>no methodology and no phases</strong> &mdash; exactly like an opportunity set to <strong>None</strong>, they are always one flat group. Global is <strong>off by default</strong>. Governance events, risks and budget stay <strong>project-level</strong> across scopes."
      },
      {
        label: 'Work item progress states',
        type: 'defs',
        items: [
          { term: 'Not Started', color: '#9e9e9e', def: "Work has not begun." },
          { term: 'In Progress', color: '#1976d2', def: "Under way." },
          { term: 'Blocked', color: '#e53935', def: "An impediment is holding it up." },
          { term: 'Done', color: '#43a047', def: "Finished." }
        ],
        note: "<strong>Status and progress are separate, and status is yours.</strong> Set it in the <strong>Details</strong> pane; it is never derived from the progress value, so moving the slider to 100% no longer flips a work item to Done on its own. Progress is what initiative completion counts &mdash; every in-scope work item must reach 100%. Overdue = not Done past its due date."
      },
      {
        label: 'Marking an initiative complete',
        type: 'defs',
        items: [
          { term: 'Mark complete', color: '#2e7d32', def: "Records the initiative as <strong>complete</strong>. The button is always clickable: if any in-scope work item is below 100% it returns a message naming exactly how many are outstanding. The same rule is enforced on the server, so the screen and the database always agree." },
          { term: 'No work items', color: '#546e7a', def: "An initiative with <em>no</em> work items reads as 100% by vacuous truth, so the rollup cannot vouch for it. It can still be marked complete, but you are asked to record what completing it meant &mdash; which is the normal case for a <strong>None</strong> initiative that tracks no artifacts." },
          { term: 'Mark not complete', color: '#ef6c00', def: "Reverses it: the initiative returns to <em>In Progress</em>, its completion stamp is cleared, and the project can no longer be completed until it is complete again." }
        ],
        note: "Completion is a <strong>decision, not a calculation</strong> &mdash; nothing here is ever set by the system. Every change is written to the <strong>Audit Trail</strong>. An initiative that is not complete <strong>blocks</strong> the project: <strong>Project Completion</strong> refuses to set a project to <em>Completed</em> until every one of its initiatives is complete."
      },
      {
        label: 'Governance event types',
        type: 'defs',
        items: [
          { term: 'Steering Co.', color: '#c62828', def: "Steering Committee &mdash; senior governance checkpoint." },
          { term: 'Gate Review', color: '#00838f', def: "Phase quality-gate review and sign-off." },
          { term: 'Milestone', color: '#ef6c00', def: "Key date; phase-end milestones also appear automatically (read-only)." },
          { term: 'Meeting', color: '#5e35b1', def: "Working or status meeting; can be recurring." },
          { term: 'Workshop', color: '#2e7d32', def: "Collaborative working session." }
        ],
        note: "Events feed the <strong>Calendar</strong> and the <strong>Next Milestone</strong> KPI. Risks carry severity and a status from Open through Resolved/Closed."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' },
      { label: 'Next: Budget Tracker →', href: '/project_management/program-management/budget-resources/budget-tracker' }
    ]
  };

export function ExecutionControlCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState<EccData | null>(null);
  const [opps, setOpps] = useState<OppOption[]>([]);
  const [scopeOpp, setScopeOpp] = useState(UNSET);

  /*
   * COMPLETION FOR THE INITIATIVE CURRENTLY IN SCOPE.
   *
   * Read from `/initiatives/closure`, the same rollup Project Completion uses, so the two
   * screens can never disagree about whether an initiative is finished. That endpoint
   * performs NO writes — it reports, and it is what enables the button. Completing is a
   * separate, explicit POST.
   */
  const [rollup, setRollup] = useState<EccInitiative[]>([]);

  /**
   * Mark the scoped initiative complete, or not complete.
   *
   * Posts to the same endpoint Project Completion uses — one write path, so the two screens
   * cannot diverge. `screen` is carried into the audit entry so the trail records where the
   * decision was made.
   */
  const markScoped = async (i: EccInitiative, outcome: 'Completed' | 'reopen') => {
    let note: string | null = null;
    if (outcome === 'Completed' && i.no_work_items) {
      // Zero work items reads as 100% by vacuous truth; the rollup cannot vouch for it.
      note = window.prompt(
        `"${i.name}" has no work items. Add a note saying what completing it means:`,
        '',
      );
      if (note == null || !note.trim()) return;
    }
    const r = await pmPost<{ success: boolean; error?: string }>('/tracker/opportunity/close', {
      delivery_project_id: projectId,
      opportunity_id: i.opportunity_id,
      outcome,
      closure_note: note,
      screen: 'execution-control-center',
    });
    if (!r.success) {
      setError(r.error || 'Could not update the initiative');
      return;
    }
    setError('');
    await loadRollup(projectId);
  };

  const loadRollup = useCallback(async (pid: string) => {
    if (!pid) {
      setRollup([]);
      return;
    }
    try {
      const r = await pmGet<{ success: boolean; initiatives?: EccInitiative[] }>(
        `/initiatives/closure?delivery_project_id=${encodeURIComponent(pid)}`,
      );
      setRollup(r.initiatives ?? []);
    } catch {
      // A rollup that cannot be read must not take the screen down with it — the work-item
      // list is what this screen is for, and it is unaffected.
      setRollup([]);
    }
  }, []);

  // Declared as a dependency rather than suppressed: `loadRollup` is a useCallback with an
  // empty dep array, so it is referentially stable and this runs only when the project does.
  useEffect(() => {
    void loadRollup(projectId);
  }, [projectId, loadRollup]);
  const [includeGlobal, setIncludeGlobal] = useState(false);

  const [deliverables, setDeliverables] = useState<EccDeliverable[]>([]);
  const [events, setEvents] = useState<EccEvent[]>([]);
  const [risks, setRisks] = useState<EccRisk[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selId, setSelId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('overview');
  const [calY, setCalY] = useState(new Date().getFullYear());
  const [calM, setCalM] = useState(new Date().getMonth());
  const [brStats, setBrStats] = useState<BrStats | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingRiskId, setEditingRiskId] = useState(0);
  const [riskForm, setRiskForm] = useState<EccRisk | null>(null);
  const [newNote, setNewNote] = useState('');
  const [eventForm, setEventForm] = useState<Partial<EccEvent> | null>(null);

  const cameFromTracker = useRef(false);
  /*
   * Portfolio Exceptions' "Open in Control Center →" deep link (§3.5). It must arrive with the
   * project, the initiative AND the work item already selected, or the user has to find the
   * item again by hand — which is the round trip that screen exists to avoid.
   *
   * THE PARAMETER NAMES ARE THE ONES THIS SCREEN ALREADY READS. The source's first attempt
   * sent `delivery_project_id`; nothing here matched it, so the button appeared to do nothing.
   * `?project=` was already in use above, and `opportunity` / `item` join it. The two `_id`
   * spellings are accepted as aliases so an older link still resolves.
   *
   * They are held in refs, not state: they are read once inside the load sequence below and
   * must not re-trigger it.
   */
  const wantOpp = useRef<string | null>(null);
  const wantItem = useRef<string | null>(null);

  const isAgile = data?.project.methodology === 'Agile';

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/deliverables/projects')
      .then((d) => {
        const list = d.projects ?? [];
        setProjects(list);
        // Restore a project ONLY when returning from a Budget/Resource Tracker's back link,
        // which carries the id as ?project=. Any other arrival leaves the field empty.
        const want = searchParams.get('project') ?? searchParams.get('delivery_project_id');
        if (want && list.some((p) => p.delivery_project_id === want)) {
          cameFromTracker.current = true;
          wantOpp.current = searchParams.get('opportunity') ?? searchParams.get('opportunity_id');
          wantItem.current = searchParams.get('item');
          setProjectId(want);
          const next = new URLSearchParams(searchParams);
          // Stripped so a refresh is clean and does not re-apply a stale selection.
          ['project', 'delivery_project_id', 'opportunity', 'opportunity_id', 'item'].forEach((k) =>
            next.delete(k),
          );
          setSearchParams(next, { replace: true });
        }
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1100);
  };

  const trackerUrl = useCallback(
    (id: string, opp: string, global: boolean) => {
      /* UNSET ("nothing chosen yet"), '__all__' and '' all mean the WHOLE project. Without
         UNSET here the screen would ask the server for an opportunity literally named
         `__unset__` and come back empty. */
      const scope =
        opp === '__all__' || opp === UNSET || !opp
          ? '&all=1'
          : `&opportunity_id=${encodeURIComponent(opp)}`;
      return `/tracker/data?delivery_project_id=${encodeURIComponent(id)}${scope}${global ? '&include_global=1' : ''}`;
    },
    [],
  );

  const applyData = (d: EccData) => {
    setData(d);
    setDeliverables((d.deliverables ?? []).map((x) => ({ ...x, progress: Number(x.progress) || 0 })));
    setEvents(d.events ?? []);
    setRisks(d.risks ?? []);
  };

  /** Load a project: opportunities, tracker data, remembered state, then the utilization KPIs. */
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const o = await pmGet<{ opportunities: OppOption[] }>(
          `/tracker/opportunities?delivery_project_id=${encodeURIComponent(projectId)}`,
        );
        if (cancelled) return;
        setOpps(o.opportunities ?? []);
        const nextScope = (o.opportunities ?? []).length ? '__all__' : '';
        setScopeOpp(nextScope);

        const d = await pmGet<EccData>(trackerUrl(projectId, nextScope, includeGlobal));
        if (cancelled) return;
        if (!d.success) {
          setError(d.error ?? 'failed');
          return;
        }
        applyData(d);

        let savedState: { expanded?: string[]; mode?: Mode; selId?: number } | null = null;
        try {
          const s = sessionStorage.getItem(eccKey(projectId));
          savedState = s ? JSON.parse(s) : null;
        } catch {
          savedState = null;
        }
        const agile = d.project.methodology === 'Agile';
        const restore = cameFromTracker.current;
        cameFromTracker.current = false;
        // Agile ALWAYS starts fully collapsed; Waterfall only restores when returning from a tracker.
        setExpanded(
          new Set(
            agile || !restore
              ? []
              : (savedState?.expanded ?? []).filter((k) => d.phases.some((p) => p.key === k)),
          ),
        );
        setSelId(
          savedState?.selId && d.deliverables.some((x) => x.id === savedState!.selId)
            ? savedState.selId!
            : (d.deliverables[0]?.id ?? null),
        );
        let m: Mode = savedState?.mode ?? 'overview';
        if (!agile && m === 'backlog') m = 'overview';
        setMode(m);

        /*
         * DEEP LINK, APPLIED IN SEQUENCE. Each list only exists once the one above it has
         * loaded: the opportunity dropdown is empty until the project has loaded, and the
         * work items are empty until the opportunity scope has. So this runs here, after the
         * project's own load, and awaits the scope reload before touching the item.
         *
         * It fails SILENTLY AND PARTIALLY rather than not at all — an out-of-scope item still
         * leaves the project and initiative correctly selected — and logs what it resolved, so
         * a failure is diagnosable rather than mysterious.
         */
        const dlOpp = wantOpp.current;
        const dlItem = wantItem.current;
        wantOpp.current = null;
        wantItem.current = null;
        if (dlOpp || dlItem) {
          let items = d.deliverables;
          if (dlOpp && (o.opportunities ?? []).some((x) => x.opportunity_id === dlOpp)) {
            setScopeOpp(dlOpp);
            const sd = await pmGet<EccData>(trackerUrl(projectId, dlOpp, includeGlobal));
            if (cancelled) return;
            if (sd.success) {
              applyData(sd);
              items = sd.deliverables;
            }
          }
          if (dlItem) {
            const id = parseInt(dlItem, 10);
            /*
             * SET OUTRIGHT, never through the row-click handler — that handler TOGGLES, and by
             * this point the selection has already been defaulted to the first item in scope,
             * so deep-linking to that first item would deselect it.
             */
            if (id && items.some((x) => x.id === id)) {
              setSelId(id);
              setMode('details');
              // Scrolled into view once the row has actually rendered under the new scope.
              setTimeout(() => {
                document
                  .getElementById(`dlv-${id}`)
                  ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }, 60);
            } else {
              console.warn('[ECC] deep link: work item', dlItem, 'is not in scope — ignored');
            }
          }
          console.info('[ECC] deep link resolved', { project: projectId, opportunity: dlOpp, item: dlItem });
        }

        const md = d.project.start_date ? pd(d.project.start_date) : today();
        setCalY((md ?? today()).getFullYear());
        setCalM((md ?? today()).getMonth());

        setBrStats(null);
        const [b, r] = await Promise.all([
          pmGet<{ phases: { planned?: number; actual?: number }[] }>(
            `/budget-tracker/data?delivery_project_id=${encodeURIComponent(projectId)}`,
          ).catch(() => ({ phases: [] })),
          pmGet<{ phases: { planned_effort?: number; actual_effort?: number }[] }>(
            `/resource-tracker/data?delivery_project_id=${encodeURIComponent(projectId)}`,
          ).catch(() => ({ phases: [] })),
        ]);
        if (cancelled) return;
        const bp = (b.phases ?? []).reduce((s, p) => s + (Number(p.planned) || 0), 0);
        const ba = (b.phases ?? []).reduce((s, p) => s + (Number(p.actual) || 0), 0);
        const rp = (r.phases ?? []).reduce((s, p) => s + (Number(p.planned_effort) || 0), 0);
        const ra = (r.phases ?? []).reduce((s, p) => s + (Number(p.actual_effort) || 0), 0);
        setBrStats({
          budget: { planned: bp, actual: ba, util: bp > 0 ? Math.round((ba / bp) * 100) : 0 },
          resource: { planned: rp, actual: ra, util: rp > 0 ? Math.round((ra / rp) * 100) : 0 },
        });
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /** Re-load work items for the chosen scope. Events, risks and budget stay project-level. */
  const reloadScope = async (opp: string, global: boolean) => {
    if (!projectId) return;
    try {
      const d = await pmGet<EccData>(trackerUrl(projectId, opp, global));
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      applyData(d);
      // Preserve open nodes; a newly added 'Global' group stays collapsed and nothing else moves.
      setExpanded((prev) => new Set([...prev].filter((k) => d.phases.some((p) => p.key === k))));
      setSelId((prev) =>
        d.deliverables.some((x) => x.id === prev) ? prev : (d.deliverables[0]?.id ?? null),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    try {
      sessionStorage.setItem('eccLastProject', projectId);
      sessionStorage.setItem(
        eccKey(projectId),
        JSON.stringify({ expanded: [...expanded], mode, selId }),
      );
    } catch {
      /* storage unavailable — state simply is not remembered */
    }
  }, [projectId, expanded, mode, selId]);

  const phases = data?.phases ?? [];
  const phasePct = (key: string) => pctOf(deliverables.filter((x) => x.phase === key));
  const overallPct = () => pctOf(deliverables);
  const statusCounts = () => {
    const c: Record<string, number> = { 'Not Started': 0, 'In Progress': 0, Blocked: 0, Done: 0 };
    deliverables.forEach((x) => {
      c[x.status] = (c[x.status] ?? 0) + 1;
    });
    return c;
  };
  const overdueCount = () => {
    const t = today();
    return deliverables.filter((x) => x.status !== 'Done' && x.due && pd(x.due)! < t).length;
  };

  const saveDlv = async (x: EccDeliverable) => {
    if (!data) return;
    try {
      await pmPost('/tracker/deliverable/update', {
        id: x.id,
        delivery_project_id: data.project.delivery_project_id,
        progress: x.progress,
        status: x.status,
        notes: x.notes,
        delivered_by_date: x.due || null,
      });
      flashSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /** Drag: update the model and repaint only. The write happens on release. */
  const onProgIn = (id: number, val: string) => {
    setDeliverables((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const x = { ...d, progress: Number(val) };
        autoStatus(x);
        return x;
      }),
    );
  };
  const onProgChange = (id: number) => {
    const x = deliverables.find((d) => d.id === id);
    if (x) void saveDlv(x);
  };

  /** Unticking Done restores the progress the item had, not 0 — that is what `_prev` is for. */
  const toggleDone = (id: number, checked: boolean) => {
    let changed: EccDeliverable | null = null;
    setDeliverables((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const x = { ...d };
        if (checked) {
          if (x.progress < 100) x._prev = x.progress;
          x.progress = 100;
        } else {
          x.progress = x._prev != null ? x._prev : 0;
        }
        autoStatus(x);
        changed = x;
        return x;
      }),
    );
    if (changed) void saveDlv(changed);
  };

  const selectDlv = (id: number) => {
    if (selId === id) {
      setSelId(null);
      return;
    }
    setSelId(id);
    setMode('details');
  };

  const reloadEvents = async () => {
    if (!data) return;
    const d = await pmGet<EccData>(
      `/tracker/data?delivery_project_id=${encodeURIComponent(data.project.delivery_project_id)}`,
    );
    if (d.success) {
      setEvents(d.events ?? []);
      setRisks(d.risks ?? []);
    }
  };

  const saveEvent = async () => {
    if (!eventForm || !data) return;
    if (!String(eventForm.title || '').trim()) {
      setError('Title required');
      return;
    }
    const rec = eventForm.recurrence || 'One-time';
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/tracker/event/save', {
        id: eventForm.id || null,
        delivery_project_id: data.project.delivery_project_id,
        title: eventForm.title,
        event_type: eventForm.event_type,
        event_date: eventForm.event_date || null,
        status: eventForm.status,
        notes: eventForm.notes,
        recurrence: rec,
        recurrence_end: rec !== 'One-time' ? eventForm.recurrence_end || null : null,
        recurrence_days:
          rec === 'Weekly' || rec === 'Bi-weekly' ? eventForm.recurrence_days || null : null,
      });
      if (d.success) {
        setEventForm(null);
        await reloadEvents();
        flashSaved();
      } else setError(d.error ?? 'failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const delEvent = async (id: number) => {
    if (!data || !window.confirm('Delete this event?')) return;
    try {
      await pmPost('/tracker/event/delete', {
        id,
        delivery_project_id: data.project.delivery_project_id,
      });
      await reloadEvents();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const saveRisk = async () => {
    if (!riskForm || !data) return;
    if (!String(riskForm.title || '').trim()) {
      setError('Risk description required');
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/tracker/risk/save', {
        id: riskForm.id || null,
        delivery_project_id: data.project.delivery_project_id,
        title: riskForm.title,
        owner: riskForm.owner,
        due_date: riskForm.due_date || null,
        severity: riskForm.severity,
        status: riskForm.status,
        mitigation: riskForm.mitigation,
        resolution: riskForm.resolution,
      });
      if (d.success) {
        setEditingRiskId(0);
        setRiskForm(null);
        await reloadEvents();
        flashSaved();
      } else setError(d.error ?? 'failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const delRisk = async (id: number) => {
    if (!data || !window.confirm('Delete this risk?')) return;
    try {
      await pmPost('/tracker/risk/delete', {
        id,
        delivery_project_id: data.project.delivery_project_id,
      });
      setEditingRiskId(0);
      setRiskForm(null);
      await reloadEvents();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addRiskNote = async (id: number) => {
    if (!data || !newNote.trim()) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/tracker/risk/note/add', {
        id,
        delivery_project_id: data.project.delivery_project_id,
        text: newNote.trim(),
      });
      if (d.success) {
        setNewNote('');
        await reloadEvents();
        const fresh = (await pmGet<EccData>(
          `/tracker/data?delivery_project_id=${encodeURIComponent(data.project.delivery_project_id)}`,
        )).risks?.find((r) => r.id === id);
        if (fresh) setRiskForm(fresh);
        flashSaved();
      } else setError(d.error ?? 'failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const openRiskForm = (id: number) => {
    setEditingRiskId(id || 0);
    setRiskForm(
      id
        ? (risks.find((x) => x.id === id) ?? null)
        : {
            id: 0,
            title: '',
            owner: '',
            due_date: '',
            mitigation: '',
            resolution: '',
            status: 'Open',
            severity: 'Medium',
            notes: [],
          },
    );
  };

  const sel = deliverables.find((d) => d.id === selId) ?? null;

  /* ---------- Overview ---------- */
  const overview = () => {
    if (!data) return null;
    const overall = overallPct();
    const c = statusCounts();
    const od = overdueCount();
    const openR = risks.filter((r) => r.status !== 'Resolved' && r.status !== 'Closed');
    const crit = openR.filter((r) => r.severity === 'Critical' || r.severity === 'High').length;
    const t = today();
    const up: { d: Date; label: string; s: string }[] = [];
    deliverables.forEach((x) => {
      if (x.status !== 'Done' && x.due && pd(x.due)! >= t)
        up.push({ d: pd(x.due)!, label: x.name, s: x.due! });
    });
    events.forEach((e) => {
      if (e.status !== 'Done')
        expandOccurrences(e).forEach((ds) => {
          if (pd(ds)! >= t) up.push({ d: pd(ds)!, label: e.title, s: ds });
        });
    });
    up.sort((a, b) => a.d.getTime() - b.d.getTime());
    const nextM = up[0];

    const statCard = (icon: string, color: string, label: string, num: unknown, sub: string) => (
      <div className="kpi" key={label}>
        <i className="material-icons kpi-icon" style={{ color }}>
          {icon}
        </i>
        <div>
          <div className="lbl">{label}</div>
          <div className="big" style={{ color }}>
            {String(num)}
          </div>
          {sub ? <div className="sub">{sub}</div> : null}
        </div>
      </div>
    );
    const brKpi = (
      icon: string,
      col: string,
      label: string,
      u: BrStats['budget'] | null,
      isMoney: boolean,
    ) => (
      <div className="kpi" key={label}>
        <i className="material-icons kpi-icon" style={{ color: col }}>
          {icon}
        </i>
        <div>
          <div className="lbl">{label}</div>
          <div className="big" style={{ color: u ? utilColor(u.util) : '#90a4ae' }}>
            {u ? `${u.util}%` : '…'}
          </div>
          <div className="sub">
            {u
              ? isMoney
                ? `$${Math.round(u.actual).toLocaleString('en-US')} / $${Math.round(u.planned).toLocaleString('en-US')}`
                : `${Math.round(u.actual)} / ${Math.round(u.planned)} pd`
              : 'loading…'}
          </div>
        </div>
      </div>
    );

    const sevs = ['Critical', 'High', 'Medium', 'Low'];
    const sevCol: Record<string, string> = {
      Critical: '#c62828',
      High: '#e53935',
      Medium: '#fb8c00',
      Low: '#43a047',
    };

    return (
      <div style={{ padding: '16px 18px' }}>
        <div className="kpi-strip" style={{ display: 'flex', marginBottom: 20 }}>
          <div className="kpi kpi-ring">
            <div>
              <RingSvg pct={overall} size={64} color="#1976d2" />
            </div>
            <div>
              <div className="lbl">Overall Completion</div>
              <div className="sub">{data.project.finalized ? 'Timeline finalized' : 'Draft timeline'}</div>
            </div>
          </div>
          {statCard('inventory_2', '#37474f', 'Work Items', deliverables.length, 'total in scope')}
          {STATUSES.map((s) =>
            statCard(STATUS_ICON[s] || 'label', STATUS_COLOR[s]!, s, c[s] ?? 0, ''),
          )}
          <div className="kpi">
            <i className="material-icons kpi-icon" style={{ color: '#e53935' }}>
              warning_amber
            </i>
            <div>
              <div className="lbl">Overdue</div>
              <div className="big" style={{ color: '#e53935' }}>
                {od}
              </div>
              <div className="sub">past due, not done</div>
            </div>
          </div>
          <div className="kpi">
            <i className="material-icons kpi-icon" style={{ color: '#fb8c00' }}>
              report_problem
            </i>
            <div>
              <div className="lbl">Open Risks</div>
              <div className="big" style={{ color: '#fb8c00' }}>
                {openR.length}
              </div>
              <div className="sub">{crit ? `${crit} high/critical` : 'none high/critical'}</div>
            </div>
          </div>
          <div className="kpi">
            <span className="kpi-icon" style={{ fontSize: 32, lineHeight: 1 }}>
              🏁
            </span>
            <div>
              <div className="lbl">Next Milestone</div>
              <div className="big" style={{ fontSize: '1.1rem' }}>
                {nextM ? fmtShort(nextM.s) : '—'}
              </div>
              <div className="sub">{nextM ? nextM.label.substring(0, 28) : 'nothing upcoming'}</div>
            </div>
          </div>
          {brKpi('account_balance_wallet', '#1976d2', 'Budget Utilization', brStats?.budget ?? null, true)}
          {brKpi('groups_2', '#00897b', 'Resource Utilization', brStats?.resource ?? null, false)}
        </div>

        <div className="ov-h">Progress by Phase</div>
        {phases.length ? (
          phases.map((ph) => {
            const ds = deliverables.filter((x) => x.phase === ph.key);
            const pct = phasePct(ph.key);
            const done = ds.filter((x) => x.progress >= 100).length;
            const col = PHASE_COLORS[ph.key] || '#90a4ae';
            return (
              <div style={{ marginBottom: 11 }} key={ph.key}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12.5,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#37474f' }}>
                    {PHASE_LABEL[ph.key] || ph.label}
                  </span>
                  <span style={{ color: col, fontWeight: 700 }}>
                    {pct}% · {done}/{ds.length}
                  </span>
                </div>
                <div
                  style={{ height: 9, background: '#eceff1', borderRadius: 5, overflow: 'hidden' }}
                >
                  <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 5 }} />
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ color: '#b0bec5', fontSize: 12.5 }}>No phases.</div>
        )}

        <div style={{ marginTop: 18 }}>
          <div className="ov-h">Open Risks by Severity</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {sevs.map((sv) => (
              <div
                key={sv}
                style={{
                  flex: 1,
                  minWidth: 78,
                  textAlign: 'center',
                  background: '#fff',
                  border: '1px solid #eceff1',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: sevCol[sv] }}>
                  {openR.filter((r) => r.severity === sv).length}
                </div>
                <div style={{ fontSize: 11, color: '#90a4ae', fontWeight: 600 }}>{sv}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  /* ---------- Details ---------- */
  const details = () => {
    if (!sel)
      return (
        <div style={{ textAlign: 'center', color: '#90a4ae', padding: 50 }}>
          <i className="material-icons" style={{ fontSize: 48, color: '#cfd8dc' }}>
            description
          </i>
          <p>Select a work item on the left to see and edit its details.</p>
        </div>
      );
    const color = PHASE_COLORS[sel.phase] || '#90a4ae';
    const patch = (p: Partial<EccDeliverable>) => {
      let changed: EccDeliverable | null = null;
      setDeliverables((prev) =>
        prev.map((d) => {
          if (d.id !== sel.id) return d;
          changed = { ...d, ...p };
          return changed;
        }),
      );
      if (changed) void saveDlv(changed);
    };
    return (
      <div style={{ maxWidth: 680 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span
            className="sdot"
            style={{ width: 13, height: 13, background: STATUS_COLOR[sel.status] }}
          />
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#263238' }}>
            {sel.name}
          </h2>
        </div>
        <div style={{ marginBottom: 18 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color,
              background: `${color}1a`,
              padding: '3px 10px',
              borderRadius: 11,
            }}
          >
            {PHASE_LABEL[sel.phase] || sel.phase}
          </span>
        </div>

        <div className="det-field">
          <div className="det-label">Progress</div>
          <div className="prog-wrap">
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                type="range"
                min={0}
                max={100}
                value={sel.progress}
                className="prog"
                style={{ background: progBgVal(sel.progress) }}
                onChange={(e) => onProgIn(sel.id, e.target.value)}
                onMouseUp={() => onProgChange(sel.id)}
                onTouchEnd={() => onProgChange(sel.id)}
                onKeyUp={() => onProgChange(sel.id)}
              />
              <div className="prog-scale">
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100%</span>
              </div>
            </div>
            <span className="prog-pct">{sel.progress}%</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div className="det-field" style={{ flex: 1, minWidth: 200 }}>
            <div className="det-label">Status</div>
            <select
              className="det-input"
              value={sel.status}
              onChange={(e) => patch({ status: e.target.value })}
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="det-field" style={{ flex: 1, minWidth: 200 }}>
            <div className="det-label">Delivered By</div>
            <input
              type="date"
              className="det-input"
              value={sel.due || ''}
              onChange={(e) => patch({ due: e.target.value })}
            />
          </div>
        </div>

        <div className="det-field">
          <div className="det-label">Document (link)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              className="det-input"
              value={sel.document_link || ''}
              placeholder="https:// or intranet link"
              disabled
              style={{ background: '#f7f9fb' }}
            />
            {sel.document_link ? (
              <a
                href={sel.document_link}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                Open ↗
              </a>
            ) : null}
          </div>
          <div style={{ fontSize: 11, color: '#b0bec5', marginTop: 4 }}>
            Edit the link on the Work Item Definition screen.
          </div>
        </div>

        <div className="det-field">
          <div className="det-label">Notes</div>
          <textarea
            className="det-input"
            rows={4}
            placeholder="Progress notes, blockers, decisions…"
            defaultValue={sel.notes || ''}
            onBlur={(e) => patch({ notes: e.target.value })}
          />
        </div>
      </div>
    );
  };

  /* ---------- Event form ---------- */
  const eventFormEl = () => {
    if (!eventForm) return null;
    const rec = eventForm.recurrence || 'One-time';
    const isRec = rec !== 'One-time';
    const isWk = rec === 'Weekly' || rec === 'Bi-weekly';
    const selDays = (eventForm.recurrence_days || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const occ = expandOccurrences(eventForm);
    const t = today();
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #d5dde3',
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 220 }}>
            <div className="det-label">Title</div>
            <input
              type="text"
              className="det-input"
              value={eventForm.title || ''}
              placeholder="e.g., Steering Committee #2"
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div className="det-label">Type</div>
            <select
              className="det-input"
              value={eventForm.event_type}
              onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
            >
              {EV_TYPES.map((tp) => (
                <option key={tp}>{tp}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div className="det-label">Start Date</div>
            <input
              type="date"
              className="det-input"
              value={eventForm.event_date || ''}
              onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div className="det-label">Occurrence</div>
            <select
              className="det-input"
              value={rec}
              onChange={(e) => setEventForm({ ...eventForm, recurrence: e.target.value })}
            >
              {EV_RECUR.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          {isRec ? (
            <div style={{ flex: 1, minWidth: 140 }}>
              <div className="det-label">End Date</div>
              <input
                type="date"
                className="det-input"
                value={eventForm.recurrence_end || ''}
                onChange={(e) => setEventForm({ ...eventForm, recurrence_end: e.target.value })}
              />
            </div>
          ) : null}
          <div style={{ flex: 1, minWidth: 130 }}>
            <div className="det-label">Status</div>
            <select
              className="det-input"
              value={eventForm.status}
              onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
            >
              {['Planned', 'Done', 'Cancelled'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        {isWk ? (
          <div style={{ marginTop: 10 }}>
            <div className="det-label">Repeat on</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DOW_NAMES.map((dn) => (
                <span
                  key={dn}
                  className={`dow-chip${selDays.includes(dn) ? ' on' : ''}`}
                  onClick={() => {
                    const next = selDays.includes(dn)
                      ? selDays.filter((x) => x !== dn)
                      : [...selDays, dn];
                    setEventForm({ ...eventForm, recurrence_days: next.join(',') });
                  }}
                >
                  {dn}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ marginTop: 10 }}>
          <div className="det-label">Notes</div>
          <input
            type="text"
            className="det-input"
            value={eventForm.notes || ''}
            placeholder="Agenda, outcome, attendees…"
            onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
          />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#1976d2', fontWeight: 600 }}>
          {isRec && eventForm.event_date
            ? !eventForm.recurrence_end
              ? 'Set an End Date to generate sessions.'
              : `↻ ${occ.length} session${occ.length === 1 ? '' : 's'}${occ.length ? ` · ${fmtShort(occ[0])} → ${fmtShort(occ[occ.length - 1])}` : ''}`
            : ''}
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => setEventForm(null)}>
            Cancel
          </button>
          <button className="btn-pri" onClick={() => void saveEvent()}>
            Save Event
          </button>
        </div>
        {/* `t` is referenced so the past-session dimming rule stays visible here too. */}
        <span style={{ display: 'none' }}>{t.getFullYear()}</span>
      </div>
    );
  };

  /* ---------- Risk form ---------- */
  const riskFormEl = () => {
    if (!riskForm) return null;
    const set = (p: Partial<EccRisk>) => setRiskForm({ ...riskForm, ...p });
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #d5dde3',
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <div className="det-label">Risk description</div>
        <input
          type="text"
          className="det-input"
          value={riskForm.title}
          placeholder="What could go wrong…"
          onChange={(e) => set({ title: e.target.value })}
        />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div className="det-label">Owner</div>
            <input
              type="text"
              className="det-input"
              value={riskForm.owner || ''}
              placeholder="Risk owner"
              onChange={(e) => set({ owner: e.target.value })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div className="det-label">Resolution Due Date</div>
            <input
              type="date"
              className="det-input"
              value={riskForm.due_date || ''}
              onChange={(e) => set({ due_date: e.target.value })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div className="det-label">Severity</div>
            <select
              className="det-input"
              value={riskForm.severity}
              onChange={(e) => set({ severity: e.target.value })}
            >
              {RISK_SEV.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div className="det-label">Status</div>
            <select
              className="det-input"
              value={riskForm.status}
              onChange={(e) => set({ status: e.target.value })}
            >
              {RISK_STATUS.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <div className="det-label">Mitigation solution</div>
          <textarea
            className="det-input"
            rows={2}
            placeholder="How we reduce or avoid this risk…"
            value={riskForm.mitigation || ''}
            onChange={(e) => set({ mitigation: e.target.value })}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <div className="det-label">Resolution</div>
          <textarea
            className="det-input"
            rows={2}
            placeholder="Final resolution / outcome…"
            value={riskForm.resolution || ''}
            onChange={(e) => set({ resolution: e.target.value })}
          />
        </div>
        {riskForm.id ? (
          <div style={{ marginTop: 14 }}>
            <div className="det-label">Notes log</div>
            <div
              style={{
                maxHeight: 160,
                overflow: 'auto',
                border: '1px solid #eceff1',
                borderRadius: 6,
                padding: '8px 10px',
                background: '#fafbfc',
              }}
            >
              {(riskForm.notes ?? []).length ? (
                [...(riskForm.notes ?? [])].reverse().map((n, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 11.5,
                      marginBottom: 7,
                      paddingBottom: 7,
                      borderBottom: '1px solid #f0f2f4',
                    }}
                  >
                    <div style={{ color: '#90a4ae', fontWeight: 600 }}>
                      🗓 {n.date || ''} · 👤 {n.author || ''}
                    </div>
                    <div style={{ color: '#37474f', marginTop: 1 }}>{n.text || ''}</div>
                  </div>
                ))
              ) : (
                <div style={{ color: '#b0bec5', fontSize: 11 }}>No notes yet.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                type="text"
                className="det-input"
                placeholder="Add a dated note…"
                style={{ flex: 1 }}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addRiskNote(riskForm.id);
                  }
                }}
              />
              <button className="btn-ghost" onClick={() => void addRiskNote(riskForm.id)}>
                Add note
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              fontSize: 11,
              color: '#90a4ae',
              marginTop: 10,
              background: '#f4f8ff',
              borderRadius: 6,
              padding: '8px 10px',
            }}
          >
            💡 Save the risk first, then you can add dated notes (with author &amp; date).
          </div>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            className="btn-ghost"
            onClick={() => {
              setEditingRiskId(0);
              setRiskForm(null);
            }}
          >
            Cancel
          </button>
          <button className="btn-pri" onClick={() => void saveRisk()}>
            Save Risk
          </button>
        </div>
      </div>
    );
  };

  /* ---------- right pane ---------- */
  const rightPane = () => {
    if (!data)
      return (
        <div style={{ padding: '56px 16px', textAlign: 'center', color: '#90a4ae' }}>
          <i className="material-icons" style={{ fontSize: 54, color: '#cfd8dc' }}>
            rocket_launch
          </i>
          <p style={{ margin: '12px 0 0' }}>Pick a project to start tracking execution.</p>
        </div>
      );
    switch (mode) {
      case 'overview':
        return overview();
      case 'backlog':
        return (
          <EccBacklog
            deliveryProjectId={data.project.delivery_project_id}
            onOpenWorkspace={() =>
              navigate(
                `/project_management/program-management/scope-ownership/agile-delivery-workspace?delivery_project_id=${encodeURIComponent(data.project.delivery_project_id)}`,
              )
            }
          />
        );
      case 'gantt':
        return (
          <EccGantt
            phases={phases}
            phaseBounds={data.phase_bounds}
            deliverables={deliverables}
          />
        );
      case 'calendar':
        return (
          <EccCalendar
            calY={calY}
            calM={calM}
            phases={phases}
            phaseBounds={data.phase_bounds}
            phaseSpans={data.phase_spans}
            allOppsMode={data.all_opps}
            events={events}
            onMove={(n) => {
              let m = calM + n;
              let y = calY;
              if (m < 0) {
                m = 11;
                y--;
              }
              if (m > 11) {
                m = 0;
                y++;
              }
              setCalM(m);
              setCalY(y);
            }}
            onToday={() => {
              const d = today();
              setCalY(d.getFullYear());
              setCalM(d.getMonth());
            }}
            onOpenGovernance={() => setMode('governance')}
          />
        );
      case 'details':
        return details();
      case 'risk':
        return (
          <>
            {riskFormEl()}
            <EccRiskList
              risks={risks}
              editingRiskId={editingRiskId}
              onEdit={openRiskForm}
              onDelete={(id) => void delRisk(id)}
            />
          </>
        );
      case 'governance':
        return (
          <>
            {eventFormEl()}
            <EccGovernance
              phases={phases}
              phaseBounds={data.phase_bounds}
              events={events}
              onAdd={(presetType) =>
                setEventForm({
                  id: 0,
                  title: '',
                  event_type: presetType || 'Meeting',
                  event_date: '',
                  status: 'Planned',
                  notes: '',
                  recurrence: 'One-time',
                  recurrence_days: '',
                })
              }
              onEdit={(id) => setEventForm(events.find((x) => x.id === id) ?? null)}
              onDelete={(id) => void delEvent(id)}
            />
          </>
        );
      case 'budget':
      case 'resources':
        return (
          <EccUtilization
            kind={mode}
            deliveryProjectId={data.project.delivery_project_id}
            onOpenTracker={(k) =>
              navigate(
                `/project_management/program-management/budget-resources/${k === 'budget' ? 'budget-tracker' : 'resource-tracker'}?delivery_project_id=${encodeURIComponent(data.project.delivery_project_id)}&from=tracker`,
              )
            }
          />
        );
    }
  };

  return (
    <div className="pm-ecc">
      <HelpPanel help={withFinalizedRule(ECC_HELP)} />
      <div className="ecc-head">
        <div className="ecc-titleblock">
          <i className="material-icons" style={{ fontSize: '2.1rem', color: '#1976d2' }}>
            insights
          </i>
          <div>
            <h1 className="page-header__title">Execution Control Center</h1>
            <p style={{ color: '#78909c', margin: '2px 0 0', fontSize: '.95rem' }}>
              Track work item progress, deadlines, calendar, risks and governance for the selected
              project.
            </p>
          </div>
        </div>
        <div className="ecc-toolbar">
          {/* The platform's field — one label, one look, width chosen per screen. */}
          <SelectField
            label="Project"
            width="lg"
            value={projectId}
            onChange={setProjectId}
            placeholder={PROMPT.project}
            options={projects.map((p) => ({
              value: p.delivery_project_id,
              id: p.delivery_project_id,
              description: `${p.description ?? ''}${p.methodology ? ` (${p.methodology})` : ''}`.trim(),
            }))}
          />
          {data ? (
            <>
              <SelectField
                label="Opportunity"
                width="lg"
                value={scopeOpp}
                onChange={(v) => {
                  setScopeOpp(v);
                  void reloadScope(v, includeGlobal);
                }}
                placeholder={PROMPT.opportunity}
                options={
                  !opps.length
                    ? [{ value: '', label: '— No opportunities —', disabled: true }]
                    : [
                        { value: '__all__', label: 'All opportunities' },
                        ...opps.map((o) => ({
                          value: o.opportunity_id,
                          // No slice(0, 38) — the control wraps, so the name is shown in full.
                          id: o.opportunity_id,
                          description: `${o.name && o.name !== o.opportunity_id ? o.name : ''}${o.methodology ? ` (${o.methodology})` : ''}`.trim(),
                        })),
                      ]
                }
              />
              <div className="dl-fg dl-fg-pin">
                <label htmlFor="pm-ecc-global" style={{ cursor: 'pointer', margin: 0 }}>
                  Global
                </label>
                <input
                  id="pm-ecc-global"
                  type="checkbox"
                  checked={includeGlobal}
                  style={{ width: 16, height: 16 }}
                  onChange={(e) => {
                    setIncludeGlobal(e.target.checked);
                    void reloadScope(scopeOpp, e.target.checked);
                  }}
                />
              </div>
            </>
          ) : null}
          <span className={`saved-tag${saved ? ' show' : ''}`} style={{ marginLeft: 'auto' }}>
            ✓ Saved
          </span>
        </div>
        {error ? (
          <div style={{ color: '#c62828', fontSize: 12.5, marginTop: 6 }}>{error}</div>
        ) : null}

        {/*
          COMPLETION — only when ONE initiative is in scope.
          "All opportunities" has nothing to complete: completion is per initiative, and a
          button that acted on an ambiguous selection is how the wrong thing gets marked
          finished. The state reads Complete / Not complete; an initiative is never "closed".
        */}
        {(() => {
          /* UNSET behaves as '__all__' did — nothing chosen means the whole project, which
             is what this pane showed before. */
          if (scopeOpp === '__all__' || scopeOpp === UNSET || !scopeOpp) return null;
          const i = rollup.find((x) => x.opportunity_id === scopeOpp);
          if (!i) return null;
          const blocked = !i.closed && !i.no_work_items && !i.all_done;
          return (
            <div
              className="ecc-closure"
              style={{
                marginTop: 8,
                padding: '8px 12px',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                background: i.closed ? '#e6f4ea' : blocked ? '#feefc3' : '#e8eaed',
                color: i.closed ? '#0d652d' : blocked ? '#7a4f01' : '#3c4043',
                fontSize: 12.5,
              }}
            >
              <strong>{i.closed ? 'Complete' : 'Not complete'}</strong>
              <span>
                {i.no_work_items
                  ? 'No work items'
                  : `${i.work_items_done} of ${i.work_items_total} work items done`}
              </span>
              {i.completed_at ? (
                <span>
                  {i.completed_at}
                  {i.completed_by_name ? ` · ${i.completed_by_name}` : ''}
                </span>
              ) : null}
              {i.closure_note ? <span>“{i.closure_note}”</span> : null}
              <button
                type="button"
                className="btn-small"
                style={{ marginLeft: 'auto' }}
                title={
                  blocked
                    ? `${i.work_items_total - i.work_items_done} work item(s) still open`
                    : undefined
                }
                onClick={() => void markScoped(i, i.closed ? 'reopen' : 'Completed')}
              >
                {i.closed ? 'Mark not complete' : 'Mark complete'}
              </button>
            </div>
          );
        })()}
      </div>

      <div className="ecc-main">
        <div className="ecc-left">
          {!data ? (
            <div style={{ padding: '56px 16px', textAlign: 'center', color: '#90a4ae' }}>
              <i className="material-icons" style={{ fontSize: 54, color: '#cfd8dc' }}>
                tune
              </i>
              <p style={{ margin: '12px 0 0' }}>Select a project to load its work items.</p>
            </div>
          ) : (
            <div className="phases-list">
              <table className="lg">
                <tbody>
                  {phases.map((ph) => {
                    const ds = deliverables.filter((x) => x.phase === ph.key);
                    const color = PHASE_COLORS[ph.key] || '#90a4ae';
                    const open = expanded.has(ph.key);
                    const pct = phasePct(ph.key);
                    const barCol = pct >= 100 ? '#43a047' : pct >= 50 ? '#26a69a' : '#42a5f5';
                    const t = today();
                    return [
                      <tr
                        className={`grp ${open ? '' : 'closed'}`}
                        key={ph.key}
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(ph.key)) next.delete(ph.key);
                            else next.add(ph.key);
                            return next;
                          })
                        }
                      >
                        <td>
                          <div className="lgc">
                            <svg
                              className="caret"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2.4}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M9 6l6 6-6 6" />
                            </svg>
                            <span className="pbox" style={{ background: color }} />
                            <span className="plabel">{PHASE_LABEL[ph.key] || ph.label}</span>
                            <span className="pcount">{ds.length}</span>
                            <span className="gmeta">
                              <span className="minibar">
                                <i
                                  style={{
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg,#42a5f5,${barCol})`,
                                  }}
                                />
                              </span>
                              <span className="minipct">{pct}%</span>
                            </span>
                          </div>
                        </td>
                      </tr>,
                      ...(open
                        ? ds.map((x) => {
                            const overdue = x.status !== 'Done' && x.due && pd(x.due)! < t;
                            const dcls = x.status === 'Done' ? 'done' : overdue ? 'over' : 'ok';
                            const isAct = x.work_item_type === 'Activity';
                            // `id` is the source's own (`dlv-<id>`) and is what a deep link
                            // scrolls to — see §3.5 in the load effect above.
                            return (
                              <tr
                                id={`dlv-${x.id}`}
                                className={`dlv ${selId === x.id ? 'sel' : ''}`}
                                key={x.id}
                              >
                                <td>
                                  <div className="d-top">
                                    <span
                                      className="sdot"
                                      style={{ background: STATUS_COLOR[x.status] || '#9e9e9e' }}
                                      title={x.status}
                                    />
                                    <span
                                      className={`wit-badge ${isAct ? 'a' : 'd'}`}
                                      title={isAct ? 'Activity' : 'Deliverable'}
                                    >
                                      {isAct ? 'A' : 'D'}
                                    </span>
                                    <span className="d-name" onClick={() => selectDlv(x.id)}>
                                      {x.name}
                                    </span>
                                    {x.due ? (
                                      <span className={`due ${dcls}`}>{fmtShort(x.due)}</span>
                                    ) : null}
                                  </div>
                                  <div className="d-prog">
                                    <input
                                      type="range"
                                      min={0}
                                      max={100}
                                      value={x.progress}
                                      className="prog"
                                      style={{ background: progBgVal(x.progress) }}
                                      onChange={(e) => onProgIn(x.id, e.target.value)}
                                      onMouseUp={() => onProgChange(x.id)}
                                      onTouchEnd={() => onProgChange(x.id)}
                                      onKeyUp={() => onProgChange(x.id)}
                                    />
                                    <span className="pct">{x.progress}%</span>
                                    <label className="done-chk" title="Mark complete">
                                      <input
                                        type="checkbox"
                                        checked={x.progress >= 100}
                                        onChange={(e) => toggleDone(x.id, e.target.checked)}
                                      />
                                      Done
                                    </label>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        : []),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ecc-right">
          <div className="modebar">
            {MODES.filter((m) => m.key !== 'backlog' || isAgile).map((m) => (
              <button
                key={m.key}
                className={`mode-btn${mode === m.key ? ' on' : ''}`}
                onClick={() => {
                  setEditingRiskId(0);
                  setRiskForm(null);
                  setEventForm(null);
                  setMode(m.key);
                }}
              >
                <i className="material-icons" style={{ fontSize: 18 }}>
                  {m.icon}
                </i>{' '}
                {m.label}
              </button>
            ))}
          </div>
          <div className="mode-body">{rightPane()}</div>
        </div>
      </div>
    </div>
  );
}
